import type React from 'react'
import { useColorPalette } from '@/contexts/color-palette-context'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePointerHandlers } from '../handlers/shape-pointer-handlers'
import { resolveShapeBodyFill } from '../helpers/shape-body-fill'
import {
  type ShapeBodyState,
  shapeBodyClassName,
  shapeBodyCursor,
  shapeBodyPointerEvents,
} from '../helpers/shape-body-style'

interface ShapeBodyPathProps {
  shape: ShapeData
  shapePath: string
  pathRef: React.Ref<SVGPathElement>
  state: ShapeBodyState
  isTextFrame: boolean
  /** Kind-7 group hover fill, when the group hover style applies. */
  groupHoverFill: string | null
  frozenGeometryCursor: boolean
  handlers: ShapePointerHandlers
  onPointerEnter: () => void
  onPointerLeave: () => void
  /** A color swatch being dragged over this shape: paint its color instead of the shape's own fill. */
  fillPreview?: string | null
  /** Skip the hover tint, so a color just dragged or dropped here isn't hidden under it. */
  suppressHoverTint?: boolean
}

/** The shape's own filled and stroked outline — the element the user clicks, drags and hovers. */
export function ShapeBodyPath({
  shape,
  shapePath,
  pathRef,
  state,
  isTextFrame,
  groupHoverFill,
  frozenGeometryCursor,
  handlers,
  onPointerEnter,
  onPointerLeave,
  fillPreview = null,
  suppressHoverTint = false,
}: ShapeBodyPathProps) {
  const useGroupHoverStyle = groupHoverFill != null
  const palette = useColorPalette()
  const fill = fillPreview ?? resolveShapeBodyFill(shape, palette)

  return (
    <path
      ref={pathRef}
      d={shapePath}
      data-editor-shape-path="true"
      data-shape-id={shape.id}
      fill={shape.photoFill ? 'transparent' : fill}
      stroke={state.remoteLocked ? 'var(--color-page-editor-lock)' : shape.stroke || '#000000'}
      strokeWidth={shape.strokeWidth || 1}
      shapeRendering="geometricPrecision"
      className={shapeBodyClassName(state, {
        useGroupHoverStyle,
        hasPhotoFill: shape.photoFill != null,
        isTextFrame,
        suppressHoverTint,
      })}
      style={{
        pointerEvents: shapeBodyPointerEvents(state),
        ...(useGroupHoverStyle
          ? ({ '--shape-group-hover-fill': groupHoverFill } as React.CSSProperties)
          : {}),
        cursor: shapeBodyCursor(state, frozenGeometryCursor),
      }}
      onPointerDown={handlers.onPointerDown}
      onDoubleClick={handlers.onDoubleClick}
      onContextMenu={handlers.onContextMenu}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    />
  )
}
